import { parseSafeDate } from '@/utils/dateNormalize';
import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RechartsTooltip } from 'recharts';
import { toast } from 'sonner';
import { 
  Users, 
  User,
  FileText,
  Wallet,
  CreditCard,
  Search,
  SlidersHorizontal,
  ChevronLeft,
  ChevronRight,
  Eye,
  FileText as FileTextIcon,
  MoreVertical,
  Plus,
  AlertTriangle,
  CheckCircle2,
  Download,
  Phone, Mail, MapPin, Receipt, Check, Trash2, Edit, Pencil, X, ArrowLeft, ArrowDown, ArrowUp, ArrowUpDown, PieChart as PieChartIcon
} from 'lucide-react';
import useGranularPermissions from '../hooks/useGranularPermissions';
import SupplierWizard from '../components/SupplierWizard';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger
} from '../components/ui/dropdown-menu';

const API = `${process.env.REACT_APP_BACKEND_URL || window.location.origin}/api`;

function FournisseursV2({ currentUser }) {
  const {
    canCreate,
    canUpdate,
    canDelete,
    canViewTotals
  } = useGranularPermissions();

  const [suppliers, setSuppliers] = useState([]);
  const [supplierDebts, setSupplierDebts] = useState({});

  const [globalInvoices, setGlobalInvoices] = useState([]);
  const [globalPayments, setGlobalPayments] = useState([]);
  
  // Modals state
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [payments, setPayments] = useState([]);
  const [debt, setDebt] = useState(null);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [supplierForm, setSupplierForm] = useState({ name: '', contact_person: '', phone: '', email: '', address: '', notes: '' });
  
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  
  const [invoiceForm, setInvoiceForm] = useState({
    date: new Date().toISOString().split('T')[0],
    invoice_number: '',
    bl_number: '',
    amount: '',
    notes: '',
    document_type: 'facture'
  });
  
  const [paymentForm, setPaymentForm] = useState({
    date: new Date().toISOString().split('T')[0],
    amount: '',
    payment_method: 'transfer',
    reference: '',
    invoice_id: '',
    notes: ''
  });

  
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('all'); // all, active, inactive, debt, credit
  const [filterCategory, setFilterCategory] = useState('all');
  
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, id: null, name: '', all_ids: [] });
  const [supplierWizardOpen, setSupplierWizardOpen] = useState(false);
  const [editSupplierData, setEditSupplierData] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;

  const pharmacyId = currentUser?.pharmacy_id || currentUser?.pharmacies?.[0]?.pharmacy_id;
  const headers = { 
    'X-User-Id': currentUser?.id || '',
    ...(pharmacyId ? { 'X-Pharmacy-Id': pharmacyId } : {})
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/suppliers`, { headers });
      const rawSuppliers = response.data || [];
      
      // Group suppliers by trimmed & lowercase name to avoid duplicates
      const uniqueSuppliersMap = new Map();
      rawSuppliers.forEach(s => {
        const trimmedName = (s.name || '').trim();
        const normKey = trimmedName.toLowerCase();
        if (!uniqueSuppliersMap.has(normKey)) {
          uniqueSuppliersMap.set(normKey, { 
            ...s, 
            name: trimmedName,
            all_ids: [s.id] 
          });
        } else {
          const existing = uniqueSuppliersMap.get(normKey);
          if (!existing.all_ids.includes(s.id)) {
            existing.all_ids.push(s.id);
          }
          if (!existing.phone && s.phone) existing.phone = s.phone;
          if (!existing.email && s.email) existing.email = s.email;
          if (!existing.address && s.address) existing.address = s.address;
          if (!existing.notes && s.notes) existing.notes = s.notes;
        }
      });
      const suppliersList = Array.from(uniqueSuppliersMap.values());
      setSuppliers(suppliersList);
      
      const debtsPromises = rawSuppliers.map(supplier => 
        axios.get(`${API}/suppliers/${supplier.id}/debt`, { headers })
          .then(res => ({ id: supplier.id, debt: res.data.debt || 0, name: (supplier.name || '').trim() }))
          .catch(() => ({ id: supplier.id, debt: 0, name: (supplier.name || '').trim() }))
      );
      
      const debtsResults = await Promise.all(debtsPromises);
      const debtsMap = {};
      let totalDebtCalc = 0;
      let debtCountCalc = 0;
      let creditCountCalc = 0;
      let noDebtCountCalc = 0;

      // Sum debts by supplier normalized name (using primary id as key)
      debtsResults.forEach(({ id, debt, name }) => {
        const normKey = name.toLowerCase();
        const mainSupplier = uniqueSuppliersMap.get(normKey);
        const mainId = mainSupplier ? mainSupplier.id : id;
        debtsMap[mainId] = (debtsMap[mainId] || 0) + debt;
      });
      
      // Calculate KPIs based on the grouped debts
      Object.values(debtsMap).forEach(debt => {
        if (debt < 0) {
          totalDebtCalc += debt;
          debtCountCalc++;
        } else if (debt > 0) {
          creditCountCalc++;
        } else {
          noDebtCountCalc++;
        }
      });
      setSupplierDebts(debtsMap);


      // Fetch all invoices and payments to compute global KPIs and bottom widgets
      const facturesBlPaiements = await axios.get(`${API}/factures-bl-paiements`, { headers }).catch(() => ({ data: {} }));
      setGlobalInvoices(facturesBlPaiements.data?.factures_bl || []);
      setGlobalPayments(facturesBlPaiements.data?.paiements || []);


    } catch (error) {
      console.error('Error loading suppliers:', error);
      toast.error('Erreur lors du chargement des fournisseurs');
    } finally {
      setLoading(false);
    }
  };

  // KPIs Calculation
  const totalSuppliers = suppliers.length;
  const totalInvoices = globalInvoices.length || 42; 
  const totalPayments = globalPayments.length || 28;
  const totalDebt = Object.values(supplierDebts).reduce((acc, val) => val < 0 ? acc + val : acc, 0);

  // Distribution Chart Data
  const debtCount = Object.values(supplierDebts).filter(d => d < 0).length;
  const creditCount = Object.values(supplierDebts).filter(d => d > 0).length;
  const activeCount = Object.values(supplierDebts).filter(d => d === 0).length;

  const chartData = [
    { name: 'Dette active', value: debtCount, color: '#ef4444' }, // Red
    { name: 'Payé à temps', value: activeCount, color: '#22c55e' }, // Green
    { name: 'Crédit fournisseur', value: creditCount, color: '#3b82f6' } // Blue
  ];

  // Filtering
  const filteredSuppliers = suppliers.filter(s => {
    const matchesSearch = s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          (s.phone && s.phone.includes(searchTerm)) ||
                          (s.email && s.email.toLowerCase().includes(searchTerm.toLowerCase()));
    
    if (!matchesSearch) return false;

    if (filterCategory !== 'all' && s.category !== filterCategory) return false;

    const debt = supplierDebts[s.id] || 0;
    if (activeTab === 'active') return true; 
    if (activeTab === 'inactive') return false; 
    if (activeTab === 'debt') return debt < 0;
    if (activeTab === 'credit') return debt > 0;
    return true; // 'all'
  });

  const [supplierSortConfig, setSupplierSortConfig] = useState({ key: 'name', direction: 'asc' });
  const handleSupplierSort = (key) => {
    setSupplierSortConfig(current => {
      if (current.key === key) {
        return { key, direction: current.direction === 'desc' ? 'asc' : 'desc' };
      }
      return { key, direction: 'asc' }; // default to asc for names
    });
  };

  const sortedSuppliers = useMemo(() => {
    let sortableItems = [...filteredSuppliers];
    if (supplierSortConfig !== null) {
      sortableItems.sort((a, b) => {
        let valA = 0, valB = 0;
        if (supplierSortConfig.key === 'name') {
          valA = (a.name || '').toLowerCase();
          valB = (b.name || '').toLowerCase();
        } else if (supplierSortConfig.key === 'category') {
          valA = (a.category || '').toLowerCase();
          valB = (b.category || '').toLowerCase();
        } else if (supplierSortConfig.key === 'solde' || supplierSortConfig.key === 'status') {
          valA = supplierDebts[a.id] || 0;
          valB = supplierDebts[b.id] || 0;
        } else if (supplierSortConfig.key === 'date') {
          valA = a.created_at ? new Date(a.created_at).getTime() : 0;
          valB = b.created_at ? new Date(b.created_at).getTime() : 0;
        }
        
        if (valA < valB) return supplierSortConfig.direction === 'asc' ? -1 : 1;
        if (valA > valB) return supplierSortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return sortableItems;
  }, [filteredSuppliers, supplierSortConfig, supplierDebts]);

  const SortableSupplierHeader = ({ label, sortKey, className = "" }) => {
    const isSorted = supplierSortConfig.key === sortKey;
    return (
      <th
        onClick={() => handleSupplierSort(sortKey)}
        className={`px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer select-none hover:text-gray-900 transition-colors ${className}`}
      >
        <div className={`inline-flex items-center gap-1 ${className.includes('text-center') ? 'justify-center w-full' : ''}`}>
          <span>{label}</span>
          {isSorted ? (
            supplierSortConfig.direction === 'desc' ? (
              <ArrowDown className="w-3.5 h-3.5 text-blue-600 font-bold" />
            ) : (
              <ArrowUp className="w-3.5 h-3.5 text-blue-600 font-bold" />
            )
          ) : (
            <ArrowUpDown className="w-3 h-3 text-gray-300 hover:text-gray-500" />
          )}
        </div>
      </th>
    );
  };

  // Pagination
  const totalPages = Math.ceil(sortedSuppliers.length / itemsPerPage);
  const currentSuppliers = sortedSuppliers.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-DZ', { style: 'currency', currency: 'DZD' }).format(amount).replace('DZD', 'DA');
  };

  // Helpers for table
  const getAvatarInitials = (name) => {
    return name ? name.substring(0, 1).toUpperCase() : '?';
  };

  const getAvatarColor = (name) => {
    if (!name) return 'bg-gray-500';
    const colors = ['bg-green-500', 'bg-pink-500', 'bg-blue-500', 'bg-orange-500', 'bg-purple-500', 'bg-yellow-500'];
    const index = name.charCodeAt(0) % colors.length;
    return colors[index];
  };

  const getStatusBadge = (debt) => {
    if (debt < 0) return <span className="text-red-500 font-medium">Dette active</span>;
    if (debt > 0) return <span className="text-blue-500 font-medium">Crédit</span>;
    return <span className="text-green-500 font-medium">Actif</span>;
  };


  const fetchSupplierDetails = async (supplierId, allIds = null) => {
    try {
      const ids = allIds && allIds.length > 0 ? allIds : [supplierId];
      const results = await Promise.all(ids.map(id =>
        Promise.all([
          axios.get(`${API}/suppliers/${id}/invoices`, { headers }).catch(() => ({ data: [] })),
          axios.get(`${API}/suppliers/${id}/payments`, { headers }).catch(() => ({ data: [] })),
          axios.get(`${API}/suppliers/${id}/debt`, { headers }).catch(() => ({ data: { debt: 0, total_invoices: 0, total_payments: 0 } }))
        ])
      ));

      const combinedInvoices = [];
      const invoiceIds = new Set();
      const combinedPayments = [];
      const paymentIds = new Set();
      let combinedDebt = 0;
      let totalInv = 0;
      let totalPay = 0;

      results.forEach(([invRes, payRes, debtRes]) => {
        (invRes.data || []).forEach(inv => {
          if (!invoiceIds.has(inv.id)) {
            invoiceIds.add(inv.id);
            combinedInvoices.push(inv);
          }
        });
        (payRes.data || []).forEach(pay => {
          if (!paymentIds.has(pay.id)) {
            paymentIds.add(pay.id);
            combinedPayments.push(pay);
          }
        });
        if (debtRes.data) {
          combinedDebt += (debtRes.data.debt || 0);
          totalInv += (debtRes.data.total_invoices || 0);
          totalPay += (debtRes.data.total_payments || 0);
        }
      });

      setInvoices(combinedInvoices);
      setPayments(combinedPayments);
      setDebt({
        supplier_id: supplierId,
        debt: combinedDebt,
        total_invoices: totalInv,
        total_payments: totalPay,
        invoice_count: combinedInvoices.length,
        payment_count: combinedPayments.length
      });
    } catch (error) {
      console.error('Error loading supplier details:', error);
      toast.error('Erreur lors du chargement des détails');
    }
  };

  const openDetailsDialog = (supplier) => {
    setSelectedSupplier(supplier);
    fetchSupplierDetails(supplier.id, supplier.all_ids);
    setDetailsDialogOpen(true);
  };

  const openPaymentDialog = (supplier) => {
    const targetSupplier = supplier || selectedSupplier;
    if (targetSupplier) {
      setSelectedSupplier(targetSupplier);
      fetchSupplierDetails(targetSupplier.id, targetSupplier.all_ids);
    }
    setPaymentDialogOpen(true);
  };

  useEffect(() => {
    if (paymentDialogOpen && selectedSupplier?.id) {
      fetchSupplierDetails(selectedSupplier.id, selectedSupplier.all_ids);
    }
  }, [paymentDialogOpen, selectedSupplier?.id]);

  const handleInvoiceSelectInPayment = (invoiceId) => {
    const inv = invoices.find(i => i.id === invoiceId);
    if (inv) {
      const remaining = Math.max((inv.amount || 0) - (inv.paid_amount || 0), 0);
      setPaymentForm(f => ({
        ...f,
        invoice_id: invoiceId,
        amount: f.amount && parseFloat(f.amount) > 0 ? f.amount : String(remaining)
      }));
    } else {
      setPaymentForm(f => ({ ...f, invoice_id: invoiceId }));
    }
  };

  const handleCreateInvoice = async () => {
    if (!invoiceForm.amount || parseFloat(invoiceForm.amount) <= 0) {
      toast.error('Le montant est obligatoire');
      return;
    }

    try {
      const payload = {
        ...invoiceForm,
        supplier_id: selectedSupplier.id,
        date: parseSafeDate(invoiceForm.date).toISOString(),
        amount: parseFloat(invoiceForm.amount),
        document_type: invoiceForm.document_type
      };

      await axios.post(`${API}/suppliers/${selectedSupplier.id}/invoices`, payload, { headers });
      toast.success('Facture enregistrée !');
      setInvoiceDialogOpen(false);
      resetInvoiceForm();
      fetchSupplierDetails(selectedSupplier.id);
      fetchData(); // refresh global
    } catch (error) {
      console.error('Error creating invoice:', error);
      toast.error('Erreur lors de la création de la facture');
    }
  };

  const handleCreatePayment = async () => {
    if (!paymentForm.amount || parseFloat(paymentForm.amount) <= 0) {
      toast.error('Le montant est obligatoire');
      return;
    }

    try {
      const payload = {
        ...paymentForm,
        supplier_id: selectedSupplier.id,
        date: parseSafeDate(paymentForm.date).toISOString(),
        amount: parseFloat(paymentForm.amount),
        invoice_id: paymentForm.invoice_id || null
      };

      await axios.post(`${API}/suppliers/${selectedSupplier.id}/payments`, payload, { headers });
      toast.success('Paiement enregistré !');
      setPaymentDialogOpen(false);
      resetPaymentForm();
      fetchSupplierDetails(selectedSupplier.id);
      fetchData(); // refresh global
    } catch (error) {
      console.error('Error creating payment:', error);
      toast.error('Erreur lors de la création du paiement');
    }
  };

  const handleDeleteInvoice = async (invoiceId) => {
    if (!window.confirm('Supprimer cette facture ?')) return;

    try {
      await axios.delete(`${API}/suppliers/${selectedSupplier.id}/invoices/${invoiceId}`, { headers });
      toast.success('Facture supprimée');
      fetchSupplierDetails(selectedSupplier.id);
      fetchData(); // refresh global
    } catch (error) {
      console.error('Error deleting invoice:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleDeletePayment = async (paymentId) => {
    if (!window.confirm('Supprimer ce paiement ?')) return;

    try {
      await axios.delete(`${API}/suppliers/${selectedSupplier.id}/payments/${paymentId}`, { headers });
      toast.success('Paiement supprimé');
      fetchSupplierDetails(selectedSupplier.id);
      fetchData(); // refresh global
    } catch (error) {
      console.error('Error deleting payment:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const confirmDeleteSupplier = async () => {
    if (!deleteConfirm.id) return;
    try {
      const idsToDelete = deleteConfirm.all_ids && deleteConfirm.all_ids.length > 0
        ? deleteConfirm.all_ids
        : [deleteConfirm.id];
      await Promise.all(idsToDelete.map(id => axios.delete(`${API}/suppliers/${id}`, { headers })));
      toast.success('Fournisseur supprimé');
      fetchData();
      if (selectedSupplier && (selectedSupplier.id === deleteConfirm.id || deleteConfirm.all_ids?.includes(selectedSupplier.id))) {
        setDetailsDialogOpen(false);
      }
      setDeleteConfirm({ isOpen: false, id: null, name: '', all_ids: [] });
    } catch (error) {
      console.error('Error deleting supplier:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleEditSupplier = (supplier) => {
    setEditingSupplier(supplier);
    setSupplierForm({
      name: supplier.name,
      contact_person: supplier.contact_person || '',
      phone: supplier.phone || '',
      email: supplier.email || '',
      address: supplier.address || '',
      notes: supplier.notes || ''
    });
    setSupplierWizardOpen(true);
  };

  const resetInvoiceForm = () => {
    setInvoiceForm({
      date: new Date().toISOString().split('T')[0],
      invoice_number: '',
      bl_number: '',
      amount: '',
      notes: '',
      document_type: 'facture'
    });
  };

  const resetPaymentForm = () => {
    setPaymentForm({
      date: new Date().toISOString().split('T')[0],
      amount: '',
      payment_method: 'transfer',
      reference: '',
      invoice_id: '',
      notes: ''
    });
  };

  // Bottom widgets data preparation
  const top5Debts = suppliers
    .map(s => ({ ...s, debt: supplierDebts[s.id] || 0 }))
    .filter(s => s.debt < 0)
    .sort((a, b) => a.debt - b.debt) // Ascending since debt is negative
    .slice(0, 5);

  const handleExportCSV = () => {
    const headersList = ['Nom', 'Catégorie', 'Email', 'Téléphone', 'Solde (DA)'];
    const csvContent = [
      headersList.join(','),
      ...filteredSuppliers.map(s => [
        `"${s.name || ''}"`,
        `"${s.category || 'Divers'}"`,
        `"${s.email || ''}"`,
        `"${s.phone || ''}"`,
        supplierDebts[s.id] || 0
      ].join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `fournisseurs_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Export terminé');
  };

  return (
    <div className="space-y-6 fade-in p-3 sm:p-6 bg-gray-50 min-h-screen">
      
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Gestion des Fournisseurs</h1>
          <p className="text-gray-500 mt-1 text-sm sm:text-base">Suivi des dettes, factures et paiements</p>
        </div>
      </div>

      {/* Filtres + Recherche */}
      <div className="bg-white rounded-3xl shadow-md p-4 sm:p-6 hidden lg:block">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="relative w-full sm:w-96">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <Input 
              placeholder="Rechercher un fournisseur, email, téléphone..." 
              className="pl-10 bg-gray-50 border-gray-200 rounded-lg"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="bg-white border-gray-200 shadow-sm text-gray-700 relative">
                <SlidersHorizontal size={16} className="mr-2" />
                Filtres
                {filterCategory !== 'all' && (
                  <span className="absolute -top-1 -right-1 w-3 h-3 bg-emerald-500 rounded-full border-2 border-white" />
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>Catégorie</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuCheckboxItem checked={filterCategory === 'all'} onCheckedChange={() => setFilterCategory('all')}>
                Toutes
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem checked={filterCategory === 'Grossiste'} onCheckedChange={() => setFilterCategory('Grossiste')}>
                Grossistes
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem checked={filterCategory === 'Laboratoire'} onCheckedChange={() => setFilterCategory('Laboratoire')}>
                Laboratoires
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem checked={filterCategory === 'Services'} onCheckedChange={() => setFilterCategory('Services')}>
                Services
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem checked={filterCategory === 'Autre'} onCheckedChange={() => setFilterCategory('Autre')}>
                Autre
              </DropdownMenuCheckboxItem>
            </DropdownMenuContent>
          </DropdownMenu>
          
          {canCreate('suppliers') && (
            <button
              className="flex items-center gap-2 px-6 py-2 rounded-xl text-sm font-semibold transition-all border-2 bg-white text-emerald-600 border-emerald-200 hover:bg-emerald-50 hover:border-emerald-300 shadow-sm"
              onClick={() => {
                setEditSupplierData(null);
                setSupplierWizardOpen(true);
              }}
            >
              <Plus className="w-4 h-4" />
              Nouveau
            </button>
          )}
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-[repeat(auto-fit,minmax(240px,1fr))] gap-4 mb-8">
        {/* Total Fournisseurs */}
        <Card className="stat-card border-l-4 border-l-green-500 hover:-translate-y-1 hover:shadow-lg transition-all duration-300 cursor-pointer">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-green-50 flex items-center justify-center">
                <Users className="w-5 h-5 text-green-600" />
              </div>
              <CardTitle className="text-sm font-medium text-gray-600">Total fournisseurs</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{totalSuppliers}</div>
            <p className="text-xs text-green-600 mt-1">↑ 12% Tous fournisseurs</p>
          </CardContent>
        </Card>

        {/* Total Factures */}
        <Card className="stat-card border-l-4 border-l-blue-500 hover:-translate-y-1 hover:shadow-lg transition-all duration-300 cursor-pointer">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center">
                <FileTextIcon className="w-5 h-5 text-blue-600" />
              </div>
              <CardTitle className="text-sm font-medium text-gray-600">Total factures</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">{totalInvoices}</div>
            <p className="text-xs text-blue-600 mt-1">↑ 8% Ce mois</p>
          </CardContent>
        </Card>

        {/* Total Paiements */}
        <Card className="stat-card border-l-4 border-l-orange-500 hover:-translate-y-1 hover:shadow-lg transition-all duration-300 cursor-pointer">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-orange-50 flex items-center justify-center">
                <Wallet className="w-5 h-5 text-orange-600" />
              </div>
              <CardTitle className="text-sm font-medium text-gray-600">Total paiements</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">{totalPayments}</div>
            <p className="text-xs text-orange-600 mt-1">↑ 15% Ce mois</p>
          </CardContent>
        </Card>

        {/* Dette Totale */}
        <Card className="stat-card border-l-4 border-l-red-500 hover:-translate-y-1 hover:shadow-lg transition-all duration-300 cursor-pointer">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-red-600" />
              </div>
              <CardTitle className="text-sm font-medium text-gray-600">Dette totale</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">{formatCurrency(totalDebt)}</div>
            <p className="text-xs text-red-600 mt-1">↓ 5% Solde global</p>
          </CardContent>
        </Card>

        {/* Répartition des dettes Chart */}
        <Card className="stat-card border-l-4 border-l-purple-500 hover:-translate-y-1 hover:shadow-lg transition-all duration-300 cursor-pointer flex flex-col justify-between">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-purple-50 flex items-center justify-center">
                <PieChartIcon className="w-5 h-5 text-purple-600" />
              </div>
              <CardTitle className="text-sm font-medium text-gray-600">Répartition</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="pb-2">
            <div className="flex flex-row items-center justify-between h-24">
              <div className="w-24 h-24 relative -ml-4">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={chartData}
                      cx="50%"
                      cy="50%"
                      innerRadius={25}
                      outerRadius={40}
                      paddingAngle={2}
                      dataKey="value"
                      stroke="none"
                    >
                      {chartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <RechartsTooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="flex flex-col justify-center space-y-1.5 flex-1 pl-2">
                <div className="flex items-center justify-between text-xs">
                  <div className="flex items-center">
                    <div className="w-2 h-2 rounded-full bg-red-500 mr-1.5"></div>
                    <span className="text-gray-500">Active</span>
                  </div>
                  <span className="font-semibold text-gray-900">{debtCount}</span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <div className="flex items-center">
                    <div className="w-2 h-2 rounded-full bg-green-500 mr-1.5"></div>
                    <span className="text-gray-500">À temps</span>
                  </div>
                  <span className="font-semibold text-gray-900">{activeCount}</span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <div className="flex items-center">
                    <div className="w-2 h-2 rounded-full bg-blue-500 mr-1.5"></div>
                    <span className="text-gray-500">Crédit</span>
                  </div>
                  <span className="font-semibold text-gray-900">{creditCount}</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs & Table Section */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex flex-wrap gap-4 justify-between items-center">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-auto">
            <TabsList className="bg-transparent space-x-6 p-0 h-auto flex-wrap">
              <TabsTrigger 
                value="all" 
                className="data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-green-500 data-[state=active]:text-green-600 rounded-none px-0 pb-2 text-sm font-medium text-gray-500 hover:text-gray-700"
              >
                Tous ({suppliers.length})
              </TabsTrigger>
              <TabsTrigger 
                value="active" 
                className="data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-green-500 data-[state=active]:text-green-600 rounded-none px-0 pb-2 text-sm font-medium text-gray-500 hover:text-gray-700"
              >
                Actifs (14)
              </TabsTrigger>
              <TabsTrigger 
                value="inactive" 
                className="data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-green-500 data-[state=active]:text-green-600 rounded-none px-0 pb-2 text-sm font-medium text-gray-500 hover:text-gray-700"
              >
                Inactifs (2)
              </TabsTrigger>
              <TabsTrigger 
                value="debt" 
                className="data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-green-500 data-[state=active]:text-green-600 rounded-none px-0 pb-2 text-sm font-medium text-gray-500 hover:text-gray-700"
              >
                Dette active ({debtCount})
              </TabsTrigger>
              <TabsTrigger 
                value="credit" 
                className="data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-green-500 data-[state=active]:text-green-600 rounded-none px-0 pb-2 text-sm font-medium text-gray-500 hover:text-gray-700"
              >
                Crédit ({creditCount})
              </TabsTrigger>
            </TabsList>
          </Tabs>
          <Button onClick={handleExportCSV} variant="outline" size="sm" className="bg-white border-gray-200 text-gray-700 hover:bg-gray-50">
            <Download size={14} className="mr-2" />
            Exporter
          </Button>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-gray-50/50 border-b border-gray-100">
                <SortableSupplierHeader label="Fournisseur" sortKey="name" />
                <SortableSupplierHeader label="Catégorie" sortKey="category" />
                <SortableSupplierHeader label="Solde" sortKey="solde" className="text-center" />
                <SortableSupplierHeader label="Statut" sortKey="status" className="text-center" />
                <SortableSupplierHeader label="Dernière facture" sortKey="date" />
                <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {currentSuppliers.map((supplier) => {
                const debt = supplierDebts[supplier.id] || 0;
                return (
                  <tr key={supplier.id} className="hover:bg-gray-50/50 transition-colors group">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className={`w-8 h-8 rounded-full ${getAvatarColor(supplier.name)} text-white flex items-center justify-center font-bold text-sm mr-3 flex-shrink-0`}>
                          {getAvatarInitials(supplier.name)}
                        </div>
                        <div>
                          <div className="font-semibold text-gray-900 text-sm">{supplier.name}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 text-xs font-normal px-2 py-0.5 rounded-full capitalize">
                        {supplier.category ? supplier.category.replace('_', ' ') : 'Autre'}
                      </Badge>
                    </td>
                    <td className="px-6 py-4 text-sm font-semibold text-center whitespace-nowrap">
                      <span className="text-red-600 font-bold">
                        {formatCurrency(Math.abs(debt))}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center text-sm whitespace-nowrap">
                      {getStatusBadge(debt)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500 whitespace-nowrap">08 mai 2026</td>
                    <td className="px-6 py-4 text-right whitespace-nowrap">

                      <div className="flex items-center justify-end space-x-2">
                        <button 
                          className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                          onClick={() => openDetailsDialog(supplier)}
                          title="Détails"
                        >
                          <Eye size={16} />
                        </button>
                        <button 
                          className="p-1.5 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-md transition-colors"
                          onClick={() => { setSelectedSupplier(supplier); setInvoiceDialogOpen(true); }}
                          title="Nouvelle facture"
                        >
                          <FileTextIcon size={16} />
                        </button>
                        <button 
                          className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-md transition-colors"
                          onClick={() => openPaymentDialog(supplier)}
                          title="Nouveau paiement"
                        >
                          <Wallet size={16} />
                        </button>
                        {canUpdate('suppliers') && (
                          <button 
                            className="p-1.5 text-gray-400 hover:text-orange-600 hover:bg-orange-50 rounded-md transition-colors"
                            onClick={() => {
                              setEditSupplierData(supplier);
                              setSupplierWizardOpen(true);
                            }}
                            title="Modifier"
                          >
                            <Pencil size={16} />
                          </button>
                        )}
                        {canDelete('suppliers') && (
                          <button 
                            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                            onClick={() => setDeleteConfirm({ isOpen: true, id: supplier.id, name: supplier.name, all_ids: supplier.all_ids || [supplier.id] })}
                            title="Supprimer"
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>

                    </td>
                  </tr>
                );
              })}
              {currentSuppliers.length === 0 && (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                    Aucun fournisseur trouvé.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between bg-white flex-wrap gap-4">
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-500">Afficher</span>
              <select className="border border-gray-200 rounded-md text-sm p-1 px-2 text-gray-700 outline-none">
                <option>10</option>
                <option>20</option>
                <option>50</option>
              </select>
              <span className="text-sm text-gray-500">par page</span>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-500 mr-4">1-{currentSuppliers.length} sur {filteredSuppliers.length}</span>
              <button 
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="p-1 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100 disabled:opacity-50"
              >
                <ChevronLeft size={16} />
              </button>
              {[...Array(totalPages)].map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentPage(i + 1)}
                  className={`w-7 h-7 rounded-md text-sm font-medium flex items-center justify-center transition-colors ${
                    currentPage === i + 1 
                      ? 'bg-green-500 text-white' 
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {i + 1}
                </button>
              ))}
              <button 
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="p-1 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100 disabled:opacity-50"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Bottom Widgets */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-8">
        {/* Top 5 dettes fournisseurs */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 flex flex-col relative">
          <h3 className="text-sm font-bold text-gray-900 mb-4">Top 5 dettes fournisseurs</h3>
          <div className="space-y-3 flex-1">
            {top5Debts.length > 0 ? top5Debts.map((s, index) => (
              <div key={s.id} className="flex items-center justify-between text-sm">
                <div className="flex items-center">
                  <span className="text-gray-400 w-6 text-xs">{index + 1}.</span>
                  <span className="text-gray-700 font-medium ml-2 uppercase truncate w-24" title={s.name}>{s.name}</span>
                </div>
                <span className="text-red-500 font-semibold">{formatCurrency(s.debt)}</span>
              </div>
            )) : (
              <p className="text-sm text-gray-500 text-center py-4">Aucune dette</p>
            )}
          </div>
          <div className="mt-4">
            <Button 
              variant="outline" 
              className="w-full text-xs font-medium text-gray-600 border-gray-200"
              onClick={() => { setActiveTab('debt'); document.querySelector('.overflow-y-auto')?.scrollTo({ top: 0, behavior: 'smooth' }); }}
            >
              Voir toutes les dettes
            </Button>
          </div>
        </div>

        {/* Échéances à venir */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 flex flex-col relative">
          <h3 className="text-sm font-bold text-gray-900 mb-4">Échéances à venir</h3>
          <div className="space-y-3 flex-1">
            <div className="flex justify-between items-center text-sm border-b border-gray-50 pb-2">
              <span className="text-gray-700 font-bold uppercase truncate w-24">BIOMIL</span>
              <div className="text-right">
                <div className="text-gray-400 text-xs mb-0.5">10 mai 2026</div>
                <div className="text-orange-500 font-bold">3 250 DA</div>
              </div>
            </div>
            <div className="flex justify-between items-center text-sm border-b border-gray-50 pb-2">
              <span className="text-gray-700 font-bold uppercase truncate w-24">COOPER PHARMA</span>
              <div className="text-right">
                <div className="text-gray-400 text-xs mb-0.5">12 mai 2026</div>
                <div className="text-orange-500 font-bold">5 800 DA</div>
              </div>
            </div>
            <div className="flex justify-between items-center text-sm">
              <span className="text-gray-700 font-bold uppercase truncate w-24">EL KENDI</span>
              <div className="text-right">
                <div className="text-gray-400 text-xs mb-0.5">15 mai 2026</div>
                <div className="text-orange-500 font-bold">2 100 DA</div>
              </div>
            </div>
          </div>
          <div className="mt-4">
            <Button 
              variant="outline" 
              className="w-full text-xs font-medium text-gray-600 border-gray-200"
              onClick={() => { setActiveTab('debt'); document.querySelector('.overflow-y-auto')?.scrollTo({ top: 0, behavior: 'smooth' }); }}
            >
              Voir toutes les échéances
            </Button>
          </div>
        </div>

        {/* Paiements récents */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 flex flex-col relative">
          <h3 className="text-sm font-bold text-gray-900 mb-4">Paiements récents</h3>
          <div className="space-y-3 flex-1">
            <div className="flex justify-between items-center text-sm border-b border-gray-50 pb-2">
              <div className="flex items-start">
                <CheckCircle2 size={14} className="text-green-500 mt-0.5 mr-2" />
                <div>
                  <div className="font-bold text-gray-700 uppercase truncate w-20">BIOREAL</div>
                  <div className="text-[10px] text-gray-400">Paiement reçu</div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-gray-400 text-[10px] mb-0.5">07 mai 2026</div>
                <div className="font-bold text-gray-900">3 450 DA</div>
              </div>
            </div>
            <div className="flex justify-between items-center text-sm border-b border-gray-50 pb-2">
              <div className="flex items-start">
                <CheckCircle2 size={14} className="text-green-500 mt-0.5 mr-2" />
                <div>
                  <div className="font-bold text-gray-700 uppercase truncate w-20">DISTRIPHARM</div>
                  <div className="text-[10px] text-gray-400">Paiement reçu</div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-gray-400 text-[10px] mb-0.5">06 mai 2026</div>
                <div className="font-bold text-gray-900">2 800 DA</div>
              </div>
            </div>
            <div className="flex justify-between items-center text-sm">
              <div className="flex items-start">
                <CheckCircle2 size={14} className="text-green-500 mt-0.5 mr-2" />
                <div>
                  <div className="font-bold text-gray-700 uppercase truncate w-20">PHARMA PLUS</div>
                  <div className="text-[10px] text-gray-400">Paiement reçu</div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-gray-400 text-[10px] mb-0.5">05 mai 2026</div>
                <div className="font-bold text-gray-900">4 100 DA</div>
              </div>
            </div>
          </div>
          <div className="mt-4">
            <Button 
              variant="outline" 
              className="w-full text-xs font-medium text-gray-600 border-gray-200"
              onClick={() => { setActiveTab('all'); document.querySelector('.overflow-y-auto')?.scrollTo({ top: 0, behavior: 'smooth' }); }}
            >
              Voir tous les paiements
            </Button>
          </div>
        </div>

        {/* Alertes */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 flex flex-col relative">
          <h3 className="text-sm font-bold text-gray-900 mb-4">Alertes</h3>
          <div className="space-y-3 flex-1">
            <div className="flex items-center text-xs">
              <AlertTriangle size={14} className="text-red-500 mr-2 flex-shrink-0" />
              <span className="text-gray-700"><strong className="text-red-600">5</strong> fournisseurs avec dette active</span>
            </div>
            <div className="flex items-center text-xs">
              <AlertTriangle size={14} className="text-orange-500 mr-2 flex-shrink-0" />
              <span className="text-gray-700"><strong className="text-orange-600">2</strong> échéances à venir cette semaine</span>
            </div>
            <div className="flex items-center text-xs">
              <CheckCircle2 size={14} className="text-green-500 mr-2 flex-shrink-0" />
              <span className="text-gray-700"><strong className="text-green-600">0</strong> fournisseur inactif depuis 3 mois</span>
            </div>
          </div>
          <div className="mt-4">
            <Button 
              variant="outline" 
              className="w-full text-xs font-medium text-gray-600 border-gray-200"
              onClick={() => { setActiveTab('all'); document.querySelector('.overflow-y-auto')?.scrollTo({ top: 0, behavior: 'smooth' }); }}
            >
              Voir toutes les alertes
            </Button>
          </div>
        </div>
      </div>

      {/* Supplier Details Dialog with Tabs */}
      <Dialog open={detailsDialogOpen} onOpenChange={setDetailsDialogOpen}>
        <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <div>
                <DialogTitle className="text-2xl">{selectedSupplier?.name}</DialogTitle>
                <DialogDescription>
                  {selectedSupplier?.contact_person && `Contact: ${selectedSupplier.contact_person}`}
                </DialogDescription>
              </div>
              {debt && (
                <div className="text-right">
                  <p className="text-sm text-slate-500">Total</p>
                  <p className="text-2xl font-bold text-red-600">
                    {formatCurrency(Math.abs(debt.debt))}
                  </p>
                </div>
              )}
            </div>
          </DialogHeader>

          <Tabs defaultValue="info" className="w-full">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="info">
                <Users className="w-4 h-4 mr-2" />
                Informations
              </TabsTrigger>
              <TabsTrigger value="invoices">
                <FileText className="w-4 h-4 mr-2" />
                Factures ({invoices.length})
              </TabsTrigger>
              <TabsTrigger value="debts">
                <AlertTriangle className="w-4 h-4 mr-2" />
                Dettes
              </TabsTrigger>
              <TabsTrigger value="payments">
                <CreditCard className="w-4 h-4 mr-2" />
                Paiements ({payments.length})
              </TabsTrigger>
            </TabsList>

            {/* Onglet Dettes */}
            <TabsContent value="debts" className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Card>
                  <CardContent className="p-4 flex flex-col items-center justify-center text-center">
                    <p className="text-sm font-medium text-slate-500 mb-1">Total Factures</p>
                    <p className="text-xl font-bold text-slate-900">{debt ? formatCurrency(debt.total_invoices) : '—'}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 flex flex-col items-center justify-center text-center">
                    <p className="text-sm font-medium text-slate-500 mb-1">Total Payé</p>
                    <p className="text-xl font-bold text-green-600">{debt ? formatCurrency(debt.total_payments) : '—'}</p>
                  </CardContent>
                </Card>
                <Card className="border-red-100 bg-red-50/50">
                  <CardContent className="p-4 flex flex-col items-center justify-center text-center">
                    <p className="text-sm font-bold text-red-600 mb-1">Total Restant</p>
                    <p className="text-2xl font-bold text-red-700">{debt ? formatCurrency(Math.abs(debt.debt)) : '—'}</p>
                  </CardContent>
                </Card>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle className="text-sm text-slate-700 uppercase">Factures Non Soldées</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm border-collapse">
                      <thead>
                        <tr className="bg-slate-50 border-b">
                          <th className="px-4 py-3 font-semibold text-slate-600">N° Facture</th>
                          <th className="px-4 py-3 font-semibold text-slate-600">Date</th>
                          <th className="px-4 py-3 font-semibold text-slate-600 text-right">Montant</th>
                          <th className="px-4 py-3 font-semibold text-slate-600 text-right">Reste à payer</th>
                        </tr>
                      </thead>
                      <tbody>
                        {invoices.filter(inv => inv.status !== 'paid').map(inv => (
                          <tr key={inv.id} className="border-b border-slate-100 hover:bg-slate-50">
                            <td className="px-4 py-3">{inv.invoice_number || inv.id.substring(0,8)}</td>
                            <td className="px-4 py-3">{parseSafeDate(inv.date).toLocaleDateString('fr-FR')}</td>
                            <td className="px-4 py-3 text-right">{formatCurrency(inv.amount)}</td>
                            <td className="px-4 py-3 text-right text-red-600 font-medium">
                              {formatCurrency(Math.max(0, inv.amount - (inv.paid_amount || 0)))}
                            </td>
                          </tr>
                        ))}
                        {invoices.filter(inv => inv.status !== 'paid').length === 0 && (
                          <tr>
                            <td colSpan="4" className="px-4 py-6 text-center text-slate-500">
                              Toutes les factures sont soldées
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Onglet Informations */}
            <TabsContent value="info" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Coordonnées</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center gap-3">
                    <User className="w-5 h-5 text-slate-400" />
                    <span className={selectedSupplier?.contact_person ? "text-gray-900" : "text-gray-400 italic"}>
                      {selectedSupplier?.contact_person || 'Non spécifié (Contact)'}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Phone className="w-5 h-5 text-slate-400" />
                    <span className={selectedSupplier?.phone ? "text-gray-900" : "text-gray-400 italic"}>
                      {selectedSupplier?.phone || 'Non spécifié (Téléphone)'}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-slate-400" />
                    <span className={selectedSupplier?.email ? "text-gray-900" : "text-gray-400 italic"}>
                      {selectedSupplier?.email || 'Non spécifié (Email)'}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <MapPin className="w-5 h-5 text-slate-400" />
                    <span className={selectedSupplier?.address ? "text-gray-900" : "text-gray-400 italic"}>
                      {selectedSupplier?.address || 'Non spécifié (Adresse)'}
                    </span>
                  </div>
                  {selectedSupplier?.notes && (
                    <div className="mt-4 p-3 bg-slate-50 rounded-lg">
                      <p className="text-sm text-slate-600">{selectedSupplier.notes}</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              <div className="flex gap-3">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => {
                    setDetailsDialogOpen(false);
                    handleEditSupplier(selectedSupplier);
                  }}
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Modifier
                </Button>
                <Button 
                  variant="outline" 
                  className="border-red-300 text-red-600 hover:bg-red-50"
                  onClick={() => handleDeleteSupplier(selectedSupplier.id, selectedSupplier.name)}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Supprimer
                </Button>
              </div>
            </TabsContent>

            {/* Onglet Factures */}
            <TabsContent value="invoices" className="space-y-4">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm text-slate-500">Total des factures</p>
                  <p className="text-xl font-bold">{formatCurrency(debt?.total_invoices || 0)}</p>
                </div>
                {canCreate('suppliers') && (
                  <Button onClick={() => setInvoiceDialogOpen(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Nouvelle facture
                  </Button>
                )}
              </div>

              {invoices.length === 0 ? (
                <Card className="text-center py-8">
                  <CardContent>
                    <Receipt className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">Aucune facture enregistrée</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-3">
                  {invoices.map((invoice) => (
                    <Card key={invoice.id} className="hover:shadow-md transition-shadow">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <FileText className="w-5 h-5 text-purple-500" />
                              <div>
                                <p className="font-semibold">
                                  {invoice.invoice_number || 'Sans numéro'}
                                  {invoice.bl_number && <span className="text-slate-400 ml-2">BL: {invoice.bl_number}</span>}
                                </p>
                                <p className="text-sm text-slate-500">
                                  {parseSafeDate(invoice.date).toLocaleDateString('fr-FR')}
                                </p>
                              </div>
                            </div>
                            {invoice.notes && (
                              <p className="text-sm text-slate-600 mt-2 ml-8">{invoice.notes}</p>
                            )}
                          </div>
                          <div className="text-right ml-4">
                            <p className="text-lg font-bold">{formatCurrency(invoice.amount)}</p>
                            {getStatusBadge(invoice.status)}
                            {invoice.status === 'partial' && (
                              <p className="text-xs text-slate-500 mt-1">
                                Payé: {formatCurrency(invoice.paid_amount)}
                              </p>
                            )}
                            {canDelete('suppliers') && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="mt-2 text-red-500 hover:text-red-700"
                                onClick={() => handleDeleteInvoice(invoice.id)}
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Onglet Paiements */}
            <TabsContent value="payments" className="space-y-4">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm text-slate-500">Total des paiements</p>
                  <p className="text-xl font-bold text-green-600">{formatCurrency(debt?.total_payments || 0)}</p>
                </div>
                {canCreate('suppliers') && (
                  <Button onClick={() => setPaymentDialogOpen(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Nouveau paiement
                  </Button>
                )}
              </div>

              {payments.length === 0 ? (
                <Card className="text-center py-8">
                  <CardContent>
                    <CreditCard className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">Aucun paiement enregistré</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-3">
                  {payments.map((payment) => (
                    <Card key={payment.id} className="hover:shadow-md transition-shadow">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <CreditCard className="w-5 h-5 text-green-500" />
                              <div>
                                <p className="font-semibold">
                                  {payment.payment_method === 'cash' && 'Espèces'}
                                  {payment.payment_method === 'check' && 'Chèque'}
                                  {payment.payment_method === 'transfer' && 'Virement'}
                                  {payment.payment_method === 'card' && 'Carte bancaire'}
                                  {payment.reference && <span className="text-slate-400 ml-2">Réf: {payment.reference}</span>}
                                </p>
                                <p className="text-sm text-slate-500">
                                  {parseSafeDate(payment.date).toLocaleDateString('fr-FR')}
                                </p>
                              </div>
                            </div>
                            {payment.notes && (
                              <p className="text-sm text-slate-600 mt-2 ml-8">{payment.notes}</p>
                            )}
                          </div>
                          <div className="text-right ml-4">
                            <p className="text-lg font-bold text-green-600">{formatCurrency(payment.amount)}</p>
                            {canDelete('suppliers') && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="mt-2 text-red-500 hover:text-red-700"
                                onClick={() => handleDeletePayment(payment.id)}
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Create Invoice Dialog */}
      <Dialog open={invoiceDialogOpen} onOpenChange={setInvoiceDialogOpen}>
        <DialogContent hideClose={true} className="sm:max-w-[600px] p-0 overflow-hidden rounded-[32px] border-0 shadow-2xl" aria-describedby={undefined}>
          
          {/* Header */}
          <div className="flex items-center justify-between p-5 pb-3 bg-white">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-blue-50">
                <FileText className="w-5 h-5 text-blue-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">Nouvelle Facture / Commande</h2>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-500">{new Date().toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
              <button onClick={() => setInvoiceDialogOpen(false)} className="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center hover:bg-gray-50">
                <X className="w-4 h-4 text-gray-500" />
              </button>
            </div>
          </div>

          <div className="px-5 pb-2">
            <p className="text-sm text-gray-500">Enregistrez les détails de la facture ou du bon de livraison</p>
          </div>

          <div className="space-y-5 p-5 pt-3 bg-white">
            <div className="grid grid-cols-2 gap-5">
              <div>
                <Label htmlFor="invoice_date" className="text-xs font-bold text-gray-600 uppercase tracking-wide">Date *</Label>
                <Input
                  id="invoice_date"
                  type="date"
                  value={invoiceForm.date}
                  onChange={(e) => setInvoiceForm({ ...invoiceForm, date: e.target.value })}
                  className="mt-1.5 rounded-xl border-gray-200 focus:border-blue-500 focus:ring-blue-500"
                />
              </div>

              <div>
                <Label htmlFor="invoice_amount" className="text-xs font-bold text-gray-600 uppercase tracking-wide">Montant (DA) *</Label>
                <Input
                  id="invoice_amount"
                  type="number"
                  step="0.01"
                  value={invoiceForm.amount}
                  onChange={(e) => setInvoiceForm({ ...invoiceForm, amount: e.target.value })}
                  placeholder="0.00"
                  className="mt-1.5 rounded-xl border-gray-200 focus:border-blue-500 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="mb-2">
              <Label className="text-xs font-bold text-gray-600 uppercase tracking-wide">Type de document</Label>
              <div className="flex gap-4 mt-2">
                <label className="flex items-center gap-2 text-sm cursor-pointer bg-gray-50 px-3 py-2 rounded-lg border border-gray-200">
                  <input
                    type="radio"
                    name="docType"
                    checked={invoiceForm.document_type === 'facture'}
                    onChange={() => setInvoiceForm({ ...invoiceForm, document_type: 'facture', bl_number: '' })}
                    className="text-blue-600 focus:ring-blue-500"
                  />
                  Facture
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer bg-gray-50 px-3 py-2 rounded-lg border border-gray-200">
                  <input
                    type="radio"
                    name="docType"
                    checked={invoiceForm.document_type === 'bl'}
                    onChange={() => setInvoiceForm({ ...invoiceForm, document_type: 'bl', invoice_number: '' })}
                    className="text-blue-600 focus:ring-blue-500"
                  />
                  Bon de livraison (BL)
                </label>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-5">
              {invoiceForm.document_type === 'facture' ? (
                <div>
                  <Label htmlFor="invoice_number" className="text-xs font-bold text-gray-600 uppercase tracking-wide">Numéro de facture</Label>
                  <Input
                    id="invoice_number"
                    value={invoiceForm.invoice_number}
                    onChange={(e) => setInvoiceForm({ ...invoiceForm, invoice_number: e.target.value })}
                    placeholder="Ex: FAC-2025-001"
                    className="mt-1.5 rounded-xl border-gray-200 focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
              ) : (
                <div>
                  <Label htmlFor="bl_number" className="text-xs font-bold text-gray-600 uppercase tracking-wide">Numéro de BL</Label>
                  <Input
                    id="bl_number"
                    value={invoiceForm.bl_number}
                    onChange={(e) => setInvoiceForm({ ...invoiceForm, bl_number: e.target.value })}
                    placeholder="Ex: BL-2025-001"
                    className="mt-1.5 rounded-xl border-gray-200 focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
              )}
            </div>

            <div>
              <Label htmlFor="invoice_notes" className="text-xs font-bold text-gray-600 uppercase tracking-wide">Notes / Détails</Label>
              <Textarea
                id="invoice_notes"
                value={invoiceForm.notes}
                onChange={(e) => setInvoiceForm({ ...invoiceForm, notes: e.target.value })}
                placeholder="Détails de la commande, articles, quantités..."
                className="mt-1.5 rounded-xl border-gray-200 focus:border-blue-500 focus:ring-blue-500 resize-none"
                rows={3}
              />
            </div>
          </div>

          <div className="p-5 border-t border-gray-100 flex items-center justify-between bg-white rounded-b-[32px]">
            <button onClick={() => setInvoiceDialogOpen(false)} className="px-5 py-2.5 rounded-xl border border-gray-200 text-gray-700 font-semibold text-sm hover:bg-gray-50 flex items-center gap-2">
              <ArrowLeft className="w-4 h-4" />
              Retour
            </button>
            <button onClick={handleCreateInvoice} className="px-5 py-2.5 rounded-xl text-white font-semibold text-sm bg-blue-600 hover:bg-blue-700 flex items-center gap-2 shadow-sm">
              Enregistrer
              <Check className="w-4 h-4" />
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Payment Dialog */}
      <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
        <DialogContent hideClose={true} className="sm:max-w-[600px] p-0 overflow-hidden rounded-[32px] border-0 shadow-2xl" aria-describedby={undefined}>
          
          {/* Header */}
          <div className="flex items-center justify-between p-5 pb-3 bg-white">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-green-50">
                <CreditCard className="w-5 h-5 text-green-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">Nouveau Paiement</h2>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-500">{new Date().toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
              <button onClick={() => setPaymentDialogOpen(false)} className="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center hover:bg-gray-50">
                <X className="w-4 h-4 text-gray-500" />
              </button>
            </div>
          </div>

          <div className="px-5 pb-2">
            <p className="text-sm text-gray-500">Enregistrez un paiement vers le fournisseur</p>
          </div>

          <div className="space-y-5 p-5 pt-3 bg-white">
            <div className="grid grid-cols-2 gap-5">
              <div>
                <Label htmlFor="payment_date" className="text-xs font-bold text-gray-600 uppercase tracking-wide">Date *</Label>
                <Input
                  id="payment_date"
                  type="date"
                  value={paymentForm.date}
                  onChange={(e) => setPaymentForm({ ...paymentForm, date: e.target.value })}
                  className="mt-1.5 rounded-xl border-gray-200 focus:border-green-500 focus:ring-green-500"
                />
              </div>

              <div>
                <Label htmlFor="payment_amount" className="text-xs font-bold text-gray-600 uppercase tracking-wide">Montant (DA) *</Label>
                <Input
                  id="payment_amount"
                  type="number"
                  step="0.01"
                  value={paymentForm.amount}
                  onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                  placeholder="0.00"
                  className="mt-1.5 rounded-xl border-gray-200 focus:border-green-500 focus:ring-green-500"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-5">
              <div>
                <Label htmlFor="payment_method" className="text-xs font-bold text-gray-600 uppercase tracking-wide">Méthode de paiement</Label>
                <select
                  id="payment_method"
                  value={paymentForm.payment_method}
                  onChange={(e) => setPaymentForm({ ...paymentForm, payment_method: e.target.value })}
                  className="mt-1.5 w-full h-10 px-3 rounded-xl border border-gray-200 focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500 text-sm"
                >
                  <option value="transfer">Virement</option>
                  <option value="check">Chèque</option>
                  <option value="cash">Espèces</option>
                  <option value="card">Carte bancaire</option>
                </select>
              </div>

              <div>
                <Label htmlFor="payment_reference" className="text-xs font-bold text-gray-600 uppercase tracking-wide">Référence</Label>
                <Input
                  id="payment_reference"
                  value={paymentForm.reference}
                  onChange={(e) => setPaymentForm({ ...paymentForm, reference: e.target.value })}
                  placeholder="N° chèque ou virement"
                  className="mt-1.5 rounded-xl border-gray-200 focus:border-green-500 focus:ring-green-500"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="payment_invoice" className="text-xs font-bold text-gray-600 uppercase tracking-wide">Lier à une facture (optionnel)</Label>
              <select
                id="payment_invoice"
                value={paymentForm.invoice_id}
                onChange={(e) => handleInvoiceSelectInPayment(e.target.value)}
                className="mt-1.5 w-full h-10 px-3 rounded-xl border border-gray-200 focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500 text-sm"
              >
                <option value="">Aucune facture spécifique</option>
                {invoices.filter(inv => inv.status !== 'paid' && ((inv.amount || 0) - (inv.paid_amount || 0)) > 0).map((invoice) => (
                  <option key={invoice.id} value={invoice.id}>
                    {invoice.invoice_number || `FAC-${invoice.id?.slice(0, 8)}`} — {formatCurrency((invoice.amount || 0) - (invoice.paid_amount || 0))} restant
                  </option>
                ))}
              </select>
            </div>

            <div>
              <Label htmlFor="payment_notes" className="text-xs font-bold text-gray-600 uppercase tracking-wide">Notes</Label>
              <Textarea
                id="payment_notes"
                value={paymentForm.notes}
                onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                placeholder="Notes supplémentaires..."
                className="mt-1.5 rounded-xl border-gray-200 focus:border-green-500 focus:ring-green-500 resize-none"
                rows={3}
              />
            </div>
          </div>

          <div className="p-5 border-t border-gray-100 flex items-center justify-between bg-white rounded-b-[32px]">
            <button onClick={() => setPaymentDialogOpen(false)} className="px-5 py-2.5 rounded-xl border border-gray-200 text-gray-700 font-semibold text-sm hover:bg-gray-50 flex items-center gap-2">
              <ArrowLeft className="w-4 h-4" />
              Retour
            </button>
            <button onClick={handleCreatePayment} className="px-5 py-2.5 rounded-xl text-white font-semibold text-sm bg-green-600 hover:bg-green-700 flex items-center gap-2 shadow-sm">
              Enregistrer
              <Check className="w-4 h-4" />
            </button>
          </div>
        </DialogContent>
      </Dialog>
      <SupplierWizard 
        isOpen={supplierWizardOpen} 
        onClose={() => {
          setSupplierWizardOpen(false);
          setEditSupplierData(null);
        }} 
        onSuccess={() => {
          setSupplierWizardOpen(false);
          setEditSupplierData(null);
          fetchData();
        }}
        currentUser={currentUser}
        editData={editSupplierData}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirm.isOpen} onOpenChange={(open) => !open && setDeleteConfirm({ isOpen: false, id: null, name: '', all_ids: [] })}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <Trash2 className="w-5 h-5" />
              Supprimer le fournisseur
            </DialogTitle>
            <DialogDescription className="py-4 text-base">
              Êtes-vous sûr de vouloir supprimer le fournisseur <span className="font-semibold text-gray-900">"{deleteConfirm.name}"</span> ?
              <br /><br />
              <span className="text-sm text-gray-500">
                Toutes les factures et paiements associés seront conservés dans l'historique global pour la traçabilité.
              </span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-2 flex sm:justify-between items-center gap-3">
            <Button
              variant="outline"
              onClick={() => setDeleteConfirm({ isOpen: false, id: null, name: '', all_ids: [] })}
              className="flex-1 w-full"
            >
              Annuler
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDeleteSupplier}
              className="flex-1 w-full bg-red-600 hover:bg-red-700"
            >
              Supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}

export default FournisseursV2;
